import { ChatMessageStamps } from '@/components/chat/message-stamps';
import type { ChatThreadRow } from '@/lib/chat-transcript';
import { cn } from '@/lib/utils';
import type { ChatRole } from '@matter_extractor/shared';
import { useEffect, useRef } from 'react';

interface ChatThreadProps {
  rows: ChatThreadRow[];
  /** Whose side is « mine ». Resolved server-side from the token (§5). */
  role: ChatRole;
  /** The room's closure, so a late arrival can be marked as one (§11). */
  closedAt: string | null;
}

/**
 * The thread of S3 and S4 (chat spec §8).
 *
 * **The anatomy is taken from `session/chat-thread.tsx` and not reinvented**:
 * 16 px of radius with one 8 px tail corner on the speaker's side, `shadow-md`,
 * `gray-50` for the other person and `bleu-nuit-tint` for one's own. Two
 * conversations in one product that looked like two products would be a worse
 * outcome than the one divergence §14 accepts on the composer.
 *
 * What it does NOT reuse from that file is the bubble SEQUENCING - the split on
 * a blank line, the typing beat between two halves of a turn. That machinery
 * exists to make an LLM's answer land like someone typing; here somebody IS
 * typing, and re-cutting a human's message would present her words in an
 * arrangement she did not choose. Her paragraph is her paragraph.
 *
 * Append-only, and there is no affordance to change that (§14): no edit, no
 * delete, no re-send button. That is what makes the transcript usable as a
 * trace.
 */
export function ChatThread({ rows, role, closedAt }: ChatThreadProps) {
  const endRef = useRef<HTMLDivElement | null>(null);

  // The effect reads none of these values - they are the TRIGGER. A row
  // arriving, or one leaving the queue, is exactly when the thread must scroll.
  // The directive has to be the LAST comment before the call: a suppression
  // separated from its code suppresses nothing, and Biome then reports it as
  // unused.
  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger, not read.
  useEffect(() => {
    // Optional call: jsdom has no `scrollIntoView`.
    endRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, [rows.length]);

  return (
    <div className="flex flex-1 flex-col gap-3 overflow-y-auto" role="log" aria-live="polite">
      {rows.map((row) => {
        const mine = row.kind === 'pending' || row.message.role === role;
        return (
          <div
            // The `clientMessageId` and never the index: it is the identity of a
            // message across the POST answer, the socket delta and the REST
            // re-read (§8.1), so a row keeps its element when a queued message
            // becomes a stored one instead of being torn down and rebuilt.
            key={row.message.clientMessageId}
            className={cn(
              'flex max-w-[85%] flex-col gap-1',
              mine ? 'self-end items-end' : 'self-start items-start',
            )}
          >
            <p
              className={cn(
                'm-0 whitespace-pre-wrap rounded-lg px-4 py-3 text-body',
                mine ? 'rounded-br-md' : 'rounded-bl-md',
                row.kind === 'pending'
                  ? // Outlined and unshadowed: it is on screen because she wrote
                    // it, but it has not happened for anybody else yet, and a
                    // filled bubble would claim it had.
                    'border border-dashed border-gray-300 bg-blanc text-gray-600'
                  : cn(
                      'shadow-md',
                      mine ? 'bg-bleu-nuit-tint text-bleu-nuit-deep' : 'bg-gray-50 text-gray-900',
                    ),
              )}
            >
              {row.message.text}
            </p>
            <ChatMessageStamps row={row} closedAt={closedAt} />
          </div>
        );
      })}
      <div ref={endRef} className="scroll-mb-40" data-testid="chat-thread-end" />
    </div>
  );
}
